import { BaseAPIModel, fetcher } from '@weezevent/weezjs-core';
import { ArgumentError } from '../../../utils/errors';
import { capitalizeFirstLetter } from '../../../pages/attendee/detail/components/utils';
import i18n from 'i18next';
import AttendeeModel from './../../../models/attendeeModel';

export default class OrderModel extends BaseAPIModel {
    fields() {
        return {
            id_transaction: { primaryKey: true, readOnly: true }
        };
    }

    static fromJSON(order) {
        const order_instance = super.fromJSON(order);
        order_instance.attendees = order_instance.attendees ? AttendeeModel.list(order_instance.attendees) : [];
        return order_instance;
    }

    static listUrl({ organizationId, eventId }) {
        if (!organizationId || !eventId) {
            throw new ArgumentError(`[Model][OrderModel][getAll] organizationId and eventId are mandatory`);
        }

        return `/ticket/organizations/${organizationId}/events/${eventId}/orders`;
    }

    get resolved_buyer_name() {
        if (this.object.buyer_last_name) {
            return `${this.object.buyer_last_name.toUpperCase()} ${capitalizeFirstLetter(this.object.buyer_first_name)}`;
        }
        return i18n.t('ticket.manager.nonNominative').toUpperCase();
    }

    static payment_method(order, { organizationId, eventId }) {
        return fetcher.get(`/ticket/organizations/${organizationId}/events/${eventId}/orders/${order.pk}/payment_method`);
    }

    download_invoice(organization, event) {
        return fetcher
            .get(`/ticket/organizations/${organization.id}/events/${event.id}/orders/${this.object.id_transaction}/invoice/download`)
            .then(({ response }) => window.open(response, '_blank'));
    }

    static perform_action(action, objects, params, isAsync = false) {
        if (!isAsync && !this[`perform_action_${action}`]) {
            throw new Error(`No action named ${action}`);
        }
        if (isAsync) {
            if (!this[`async_perform_action_${action}`]) {
                throw new Error(`No action named ${action}`);
            }
            return this[`async_perform_action_${action}`](objects, params);
        }
        return this[`perform_action_${action}`](objects, params);
    }

    static perform_action_send(orders, { organization, event }) {
        let promise = orders;
        return Promise.resolve(promise);
    }

    static perform_action_print_ticket(orders, { organization, event }) {
        console.warn('helle');
    }

    static perform_action_delete_refund_requests(data, { organization, event }) {
        return fetcher.delete(`/ticket/organizations/${organization.id}/events/${event.id}/refund_request`, { data: data });
    }

    static switch_status(orders, organization, event, status = 'valid') {
        const data = (orders instanceof Array ? orders : [orders]).map(order => order['id_transaction']);
        const method = status === 'valid' ? 'set_paid' : 'set_unpaid';
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/${method}`, { data });
    }

    static perform_action_unpaid(orders, { organization, event }) {
        let promise = this.switch_status(orders, organization, event, 'reservation');
        return Promise.resolve(promise);
    }

    static async_perform_action_unpaid(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_set_unpaid',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };

        return this.fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions`, { data });
    }

    static perform_action_rotate_barcode(orders, { organization, event, send_mail, to_reservation }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/rotate_barcode_order${send_mail ? '?send_mail=true' : ''}`, { data: data });
    }

    static perform_action_refund(orders, { organization, event, delete_all }) {
        const data = {
            order_ids: orders.map(({ pk }) => pk),
            options: {
                delete_refunded: delete_all,
                dry_run: false,
                refund_charges: true
            }
        };

        return this.fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/refund`, { data });
    }

    static async_perform_action_refund(orders, { organization, event, delete_all }) {
        const orders_list = orders.map(order => order['id_transaction']).join(',');
        const data = {
            type: 'async_bulk_refund',
            resource_query_filter: {
                order__in: orders_list
            },
            config: {
                delete_refunded: delete_all,
                dry_run: false,
                refund_charges: true
            }
        };
        return this.fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data });
    }

    static perform_action_paid(orders, { organization, event, send_receipt }) {
        const order_ids = (orders instanceof Array ? orders : [orders]).map(order => order.id_transaction);
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/set_paid`, { data: { order_ids, send_receipt } });
    }

    static perform_action_download_invoices(orders, { organization, event }) {
        orders[0].download_invoice(organization, event);
        return Promise.resolve(orders);
    }

    static perform_action_download_ticket_pdf(orders, { organization, event }) {
        if (orders[0]?.id_transaction !== null) {
            return fetcher
                .post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/download_order`, { data: [{ id: orders[0].id_transaction }] })
                .then(({ response }) => window.open(response, '_blank'));
        }
    }

    static perform_action_download_tickets(orders, { organization, event }) {
        const orders_list = orders.map(order => order['id_transaction']).join(',');
        return fetcher
            .get(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/download?order__in=${orders_list}`)
            .then(({ response }) => window.open(response, '_blank'));
    }

    static perform_action_anonymize(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/anonymize`, { data });
    }

    static perform_action_restore(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/restore`, { data });
    }

    static async_perform_action_restore(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_restore',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_restore`, { data });
    }

    static perform_action_send_order(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_mail`, { data });
    }

    static perform_action_send_receipt(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_receipt`, { data });
    }

    static async_perform_action_send_receipt(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_receipt',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_receipt`, { data });
    }

    static perform_action_send_payment_instruction(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_payment_instruction`, { data });
    }

    static async_perform_action_send_payment_instruction(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_payment_instruction',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_payment_instruction`, { data });
    }

    static perform_action_send_invoice(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_invoice`, { data });
    }

    static async_perform_action_send_invoice(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_invoice',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_invoice`, { data });
    }

    static perform_action_send_payment_request(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_request_payment`, { data });
    }

    static async_perform_action_send_payment_request(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_request_payment',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_request_payment`, { data });
    }

    static perform_action_send_update_info_payment_request(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_request_payment`, { data });
    }

    static async_perform_action_send_update_info_payment_request(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_request_payment',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_request_payment`, { data });
    }

    static perform_action_send_tickets_approval_buyer(orders, { organization, event }) {
        const data = orders.map(order => ({ id: order.id_transaction }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/send_tickets_approval_buyer`, { data });
    }

    static async_perform_action_send_tickets_approval_buyer(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_send_tickets_approval_buyer',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions/async_send_tickets_approval_buyer`, { data });
    }

    static perform_action_delete(orders, { organization, event }) {
        let promise = this.destroy(orders, {
            organizationId: organization.pk,
            eventId: event.pk
        });
        return Promise.resolve(promise);
    }

    static async_perform_action_delete(orders, { organization, event }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_bulk_destroy',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions`, { data });
    }

    static async_perform_action_paid(orders, { organization, event, send_receipt }) {
        let orders_ids = orders.map(element => element.id_transaction);
        const data = {
            type: 'async_set_paid',
            resource_query_filter: {
                id_transaction__in: orders_ids.join(',')
            },
            config: {
                send_receipt: send_receipt
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/orders/actions`, { data });
    }
}
